import React, { useEffect, useMemo, useRef, useState } from "react";
import { Wallet, Layers, Coins, ArrowDownToLine, Megaphone, TrendingUp, Gauge } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, StatCard, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tx, setTx] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [funding, setFunding] = useState([]);
  const [settings, setSettings] = useState(null);
  const [liveReward, setLiveReward] = useState(0);
  const baseRef = useRef(0);

  useEffect(() => {
    refresh();
    Promise.all([
      api.get("/transactions/mine"),
      api.get("/rewards/mine"),
      api.get("/withdrawals/mine"),
      api.get("/announcements"),
      api.get("/funding/mine"),
      api.get("/settings/public"),
    ]).then(([t, r, w, a, f, s]) => {
      setTx(t.data); setRewards(r.data); setWithdrawals(w.data);
      setAnnouncements(a.data); setFunding(f.data); setSettings(s.data);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activePlans = funding.filter((f) => f.status === "active");
  const totalFunding = activePlans.reduce((s, f) => s + Number(f.amount), 0);
  const withdrawableFunding = activePlans.reduce((s, f) => s + Number(f.withdrawable_amount ?? f.amount), 0);
  const hourlyRate = settings?.hourly_reward_rate ?? 0.0001375;

  // Real-time reward accumulation (display only): base balance + linear accrual since load
  useEffect(() => {
    baseRef.current = Number(user?.rewards_balance || 0);
    setLiveReward(baseRef.current);
    const perSecond = (totalFunding * hourlyRate) / 3600;
    if (perSecond <= 0) return;
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      setLiveReward(baseRef.current + perSecond * elapsed);
    }, 1000);
    return () => clearInterval(id);
  }, [user?.rewards_balance, totalFunding, hourlyRate]);

  const activityChart = useMemo(() => aggregateByDay(
    tx.map((t) => ({ date: t.created_at, amount: Number(t.amount) }))
  ), [tx]);
  const withdrawChart = useMemo(() => aggregateByDay(
    withdrawals.filter((w) => w.status === "approved").map((w) => ({ date: w.created_at, amount: Number(w.net_amount) }))
  ), [withdrawals]);

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user?.name?.split(" ")[0] || "Member"}`}
        subtitle={`User ID: ${user?.user_id || "—"} • Joined ${fmtDate(user?.created_at)}`}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Wallet Balance" value={fmtUSD(user?.wallet_balance)} accent icon={Wallet} hint="USDT (BEP20)" />
        <StatCard label="Total Contribution" value={fmtUSD(totalFunding)} icon={TrendingUp} hint={`${activePlans.length} active plan${activePlans.length === 1 ? "" : "s"}`} />
        <StatCard label="Withdrawable Funding" value={fmtUSD(withdrawableFunding)} icon={Gauge} hint="Available to withdraw" />
        <StatCard label="Active Plans" value={activePlans.length} icon={Layers} />
        <div className="glass p-4 sm:p-5 neon-glow" data-testid="live-reward-card">
          <div className="flex items-start justify-between gap-2">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] text-[#94A3B8] font-bold leading-tight">Accumulated Reward</div>
            <Coins size={16} className="text-[#00F0FF] flex-shrink-0" strokeWidth={1.6} />
          </div>
          <div className="font-display text-xl sm:text-2xl lg:text-3xl font-black mt-2 sm:mt-3 break-all text-[#00F0FF]" data-testid="live-reward-value">
            {fmtUSD(liveReward)}
          </div>
          <div className="text-[10px] sm:text-xs text-[#94A3B8] mt-1 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] pulse-glow" /> updating live
          </div>
        </div>
        <StatCard label="Total Withdrawals" value={fmtUSD(user?.total_withdrawn)} icon={ArrowDownToLine} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="glass p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Account Activity</h3>
            <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Last 14 days</span>
          </div>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={activityChart}>
                <defs>
                  <linearGradient id="rewardGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeOpacity={0.05} />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A132A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#fff" }} />
                <Area type="monotone" dataKey="amount" stroke="#00F0FF" strokeWidth={2} fill="url(#rewardGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-bold">Withdrawals</h3>
            <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Net</span>
          </div>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={withdrawChart}>
                <CartesianGrid strokeOpacity={0.05} />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0A132A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} labelStyle={{ color: "#fff" }} />
                <Bar dataKey="amount" fill="#007AFF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="glass p-5 lg:col-span-2">
          <h3 className="font-display text-lg font-bold mb-4">Participation History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
                <tr className="border-b border-white/5"><th className="text-left py-2">Date</th><th className="text-left">Type</th><th className="text-right">Amount</th><th className="text-right">Status</th></tr>
              </thead>
              <tbody data-testid="tx-table">
                {tx.slice(0, 8).map((t) => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 text-[#94A3B8]">{fmtDate(t.created_at)}</td>
                    <td className="capitalize">{t.type.replace(/_/g, " ")}</td>
                    <td className="text-right font-mono">{fmtUSD(t.amount)}</td>
                    <td className="text-right"><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
                {tx.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[#475569]">No activity yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="glass p-5">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone size={18} className="text-[#00F0FF]" />
            <h3 className="font-display text-lg font-bold">Announcements</h3>
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {announcements.slice(0, 6).map((a) => (
              <div key={a.id} className="border-l-2 border-[#00F0FF]/40 pl-3">
                <div className="font-semibold text-sm">{a.title}</div>
                <div className="text-xs text-[#94A3B8] mt-1">{a.description}</div>
                <div className="text-[10px] text-[#475569] mt-1 uppercase tracking-wider">{fmtDate(a.created_at)}</div>
              </div>
            ))}
            {announcements.length === 0 && <div className="text-[#475569] text-sm">No announcements</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function aggregateByDay(items) {
  const map = {};
  for (const it of items) {
    const k = new Date(it.date).toISOString().slice(5, 10);
    map[k] = (map[k] || 0) + it.amount;
  }
  const keys = Object.keys(map).sort();
  return keys.slice(-14).map((k) => ({ date: k, amount: Number(map[k].toFixed(2)) }));
}
